import auth0 from "auth0-js";
import { EventEmitter } from "events";
import AppConfig from "@/constants/AppConfig";
import httpStatus from "http-status";
import { i18n } from "@/i18n";
import {
	CalendarAuthorizationInitiatedEvent,
	CalendarAuthorizationCompleteEvent
} from "@/telemetry";

const MILLESECONDS = 1000;
const MAX_LISTENERS = 5;

const googleStandardOptions = {
	access_type: "offline",
	connection: "google-oauth2",
	scope: "openid profile email read:current_user read:user_idp_tokens",
};

const googleCalendarOptions = {
	access_type: "offline",
	connection_scope: AppConfig.googleCalendarPermissions,
};

const msStandardOptions = {
	access_type: "offline",
	connection: "microsoft-oauth",
	scope: "openid profile email User.Read read:current_user read:user_idp_tokens",
	prompt: "select_account",
};

const msCalendarOptions = {
	access_type: "offline",
	connection_scope: AppConfig.microsoftCalendarPermissions,
};

const eventDefaults = overrides => {
	return {
		loggedIn: false,
		user: null,
		error: null,
		state: {},
		token: "",
		redirectTo: "",
		...overrides,
	};
};

const mcPaths = /^(\/mc\/)/gi;

let instance;

export function getAuthServiceInstance() {
	if ( !instance ) {
		const webAuth = new auth0.WebAuth( {
			clientID: AppConfig.auth0ClientId,
			domain: AppConfig.auth0Domain,
			audience: AppConfig.auth0Audience,
			redirectUri: `${window.location.origin}/callback`,
			responseType: "token id_token",
			scope: "openid profile email read:current_user read:user_idp_tokens",
		} );

		const localStorageKey = "loggedIn";
		const loginEvent = "loginEvent";
		const calPermsRequestCompleted = "calPermsRequestCompleted";
		const calPermsRequestInitiated = "calPermsRequestInitiated";
		const calPermsRequestError = "calPermsRequestError";

		class AuthService extends EventEmitter {
			idToken = null
			profile = null
			tokenExpiry = null
			accessToken = null

			async handleCalendarAuthorization() {
				webAuth.popup.callback();
			}

			async authorizeCalendarIntegration( {
				customState,
				source,
				email,
				isRefreshingCredentials = false,
			} ) {
				const provider = source === "GOOGLE" ? "Google" : "Microsoft";
				const authInitiatedEvent = new CalendarAuthorizationInitiatedEvent( provider );
				await authInitiatedEvent.submit();
				return webAuth.popup.authorize( {
					appState: customState,
					...( source === "GOOGLE" ? googleStandardOptions : msStandardOptions ),
					...( source === "GOOGLE" ? googleCalendarOptions : msCalendarOptions ),
					redirectUri: `${window.location.origin}/calauth`,
					approvalPrompt: "force",
				}, async ( err, authResult ) => {
					const targetEmail = authResult?.idTokenPayload?.email;
					if ( targetEmail.toLowerCase() !== email.toLowerCase() ) {
						const diffEmailFailureEvent = new CalendarAuthorizationCompleteEvent( provider, "failed" );
						await diffEmailFailureEvent.submit();
						return this.emit( calPermsRequestError, eventDefaults( {
							email,
							targetEmail,
						} ) );
					}
					await this.localLogin( { authResult, } );
					this.emit( calPermsRequestInitiated );
					const token = await this.getIdToken();
					const payload = {};
					if ( isRefreshingCredentials ) {
						payload.renewRequired = false;
					} else {
						payload.enabledCalendarIntegration = true;
						payload.renewRequired = false;
					}
					await fetch( `${ AppConfig.baseUri }/user/me`, {
						method: "PUT",
						headers: {
							Authorization: `Bearer ${ token }`,
							"Content-Type": "application/json",
						},
						body: JSON.stringify( payload ),
					} ).then( response => {
						return response.json().then( async body => {
							if ( response.status === httpStatus.UNAUTHORIZED ) {
								localStorage.removeItem( localStorageKey );
							}
							const calAuthIntSuccessEvent = new CalendarAuthorizationCompleteEvent( provider, "succeeded" );
							await calAuthIntSuccessEvent.submit();
							this.emit( calPermsRequestCompleted, eventDefaults( {
								user: body,
								error: null,
								state: authResult.appState || {},
								token: authResult.idToken,
								redirectTo: "schedule",
							} ) );
						} );
					}, async e => {
						console.error( "an error occurred while trying to authorize calendar access", e );
						const otherCalAuthFailureEvent = new CalendarAuthorizationCompleteEvent( provider, "failed" );
						await otherCalAuthFailureEvent.submit();
						return this.emit( calPermsRequestError, {} );
					} );
				} );
			}

			logOut() {
				localStorage.removeItem( localStorageKey );

				this.idToken = null;
				this.tokenExpiry = null;
				this.profile = null;

				this.emit( loginEvent, eventDefaults( { loggedIn: false, } ) );

				webAuth.logout( {
					returnTo: `${window.location.origin}`,
				} );
			}

			isAuthenticated() {
				return Date.now() < this.tokenExpiry && localStorage.getItem( localStorageKey ) === "true";
			}

			isIdTokenValid() {
				return this.idToken && this.tokenExpiry && Date.now() < this.tokenExpiry;
			}

			getIdToken() {
				return new Promise( ( resolve, reject ) => {
					if ( this.isIdTokenValid() ) {
						resolve( this.idToken );
					} else if ( this.isAuthenticated() ) {
						this.renewTokens().then( authResult => {
							resolve( authResult.idToken );
						}, reject );
					} else {
						resolve();
					}
				} );
			}

			localLogin( { authResult, } ) {
				this.idToken = authResult.idToken;
				this.profile = authResult.idTokenPayload;
				this.accessToken = authResult.accessToken;
				// Convert the expiry time from seconds to milliseconds,
				// required by the Date constructor
				this.tokenExpiry = new Date( this.profile.exp * MILLESECONDS );

				localStorage.setItem( localStorageKey, "true" );

				const authError = new Error( i18n.t( "auth.signinErrorMessage" ) );

				fetch( `${AppConfig.baseUri}/user/me`, {
					headers: {
						Authorization: `Bearer ${authResult.idToken}`,
					},
				} ).then( response => {
					return response.json().then( body => {
						if ( response.status === httpStatus.UNAUTHORIZED ) {
							localStorage.removeItem( localStorageKey );
						}
						authError.innerError = body;
						const isLoggedIn = response.status < httpStatus.BAD_REQUEST;
						this.emit( loginEvent, eventDefaults( {
							loggedIn: isLoggedIn,
							user: isLoggedIn ? body : null,
							isEmailVerified: this.profile.email_verified,
							error: response.status >= httpStatus.BAD_REQUEST ? authError : null,
							state: authResult.appState || {},
							token: authResult.idToken,
							redirectTo: isLoggedIn ? "" : "signup",
						} ) );
					} );
				}, err => {
					console.error( "an error occurred while trying to fetch user data from the API", err );
					localStorage.removeItem( localStorageKey );
					authError.innerError = err;
					this.emit( loginEvent, eventDefaults( {
						loggedIn: false,
						error: authError,
						state: authResult.appState || {},
						token: authResult.idToken,
						redirectTo: "login",
					} ) );
				} );
			}

			renewTokens() {
				return new Promise( ( resolve, reject ) => {
					if ( localStorage.getItem( localStorageKey ) !== "true" ) {
						return reject( "Not logged in" );
					}

					webAuth.checkSession( {}, ( err, authResult ) => {
						if ( err ) {
							reject( err );
						} else {
							this.localLogin( { authResult, } );
							resolve( authResult );
						}
					} );
				} );
			}

			shouldAttemptRenewOnPath( path ) {
				mcPaths.lastIndex = 0;
				return mcPaths.test( path );
			}
		}

		instance = new AuthService();

		// Temporary - will be removed after testing is complete
		window.__SERVICE__ = instance;

		instance.setMaxListeners( MAX_LISTENERS );
	}

	return instance;
}



// WEBPACK FOOTER //
// ./src/helpers/authService.js